/**
 * /sim-only-deals — SEO landing page (companion to /sim-only)
 * Target: "SIM only deals", "SIM only deals UK", "best SIM only deals 2024", "SIM only plans"
 * Research: Highest volume SIM keyword (90-110k/mo UK). Competitors all fight here.
 * Silux angle: Three-powered, 5G included, Go Roam, no contract — stronger proposition.
 */
import { Link } from 'react-router-dom'
import { CheckCircle, ArrowRight, Smartphone, Globe, Zap, Shield, Star } from 'lucide-react'
import SEOHead from '@/components/SEOHead'
import SiluxPromise from '@/components/SiluxPromise'

const FAQS = [
  {
    question: 'What are SIM only deals?',
    answer: 'SIM only deals are mobile phone plans where you get just the SIM card — no handset included. You keep your existing phone, pay less per month, and choose from flexible monthly plans or 12-month contracts.',
  },
  {
    question: 'What is the best SIM only deal in the UK?',
    answer: 'The best SIM only deal depends on your needs. Silux Mobile offers competitive SIM only deals on Three, Vodafone, EE and O2, unlimited data options, and a choice of 30-day rolling plans or 3, 6, 12 or 24-month contracts from as little as £5/month.',
  },
  {
    question: 'Do SIM only deals include 5G?',
    answer: 'Yes — all Silux Mobile SIM only deals include 5G at no extra charge. 5G is available on Three, Vodafone, EE and O2 in major UK cities and is expanding rapidly.',
  },
  {
    question: 'Can I use a SIM only deal with any phone?',
    answer: 'Yes — our SIM cards work with any unlocked smartphone, tablet, or device. If your phone is locked to another network, you\'ll need to unlock it first.',
  },
  {
    question: 'How do SIM only deals save me money?',
    answer: 'By not paying for a handset, you only pay for your minutes, texts and data — making monthly bills significantly cheaper. Many customers save £20-30/month by switching to SIM only.',
  },
  {
    question: 'Will my price increase during my contract?',
    answer: 'No. Silux Mobile never raises your price mid-contract — there are no price increases during your contract and no annual or inflation-linked price rises. The price you sign up at is the price you pay.',
  },
  {
    question: 'Are plan and SIM upgrades free?',
    answer: 'Yes. Upgrades are free — move to a bigger data plan or a better SIM whenever you like at no extra cost, with no upgrade or admin fees.',
  },
  {
    question: 'Do you run a credit check?',
    answer: 'No. There are no credit checks on our SIM contracts, so you can join Silux Mobile regardless of your credit history. No credit check is required to order or activate your SIM.',
  },
  {
    question: 'Is Silux Mobile a good mobile provider?',
    answer: 'Yes. Silux Mobile is a UK SIM-only and mobile network provider with SIMs on Three, Vodafone, EE and O2, with cheap SIM deals, no price rises, free upgrades and no credit checks on SIM contracts.',
  },
]

const PLAN_TYPES = [
  {
    slug: '/sim-only/calls-text-data',
    name: 'Calls, Texts & Data',
    desc: 'Full UK calling, texts and data bundle — ideal for everyday use',
    icon: Smartphone,
    color: 'primary',
  },
  {
    slug: '/sim-only/data-only',
    name: 'Data Only SIM',
    desc: 'Pure data for tablets, MiFi devices and mobile broadband',
    icon: Zap,
    color: 'blue',
  },
  {
    slug: '/sim-only/multisim',
    name: 'Multi-SIM Deals',
    desc: 'Multiple SIMs on one bill — perfect for families or businesses',
    icon: Globe,
    color: 'green',
  },
  {
    slug: '/sim-only/roaming',
    name: 'Roaming SIM',
    desc: 'Use your SIM abroad in 71+ countries with Three Go Roam — roaming is an extra cost and varies by package',
    icon: Globe,
    color: 'purple',
  },
]

export default function SimOnlyDeals() {
  const breadcrumbs = [
    { name: 'Home', url: 'https://www.siluxmobile.co.uk/' },
    { name: 'SIM Only Deals', url: 'https://www.siluxmobile.co.uk/sim-only-deals' },
  ]

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: FAQS.map(faq => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: { '@type': 'Answer', text: faq.answer },
    })),
  }

  return (
    <>
      <SEOHead
        title="SIM Only Deals UK 2026 | Best SIM Only Plans | Silux Mobile"
        description="Find the best SIM only deals in the UK from Silux Mobile. 5G included, Go Roam in 71+ countries, no contracts, from £5/month. Data SIMs, calls & text SIMs, multi-SIM plans. No price rises, free upgrades and no credit checks on SIM contracts."
        keywords={[
          'SIM only deals', 'SIM only deals UK', 'best SIM only deals', 'SIM only plans UK',
          'cheap SIM only', 'SIM only contract UK', 'best SIM only deal 2026',
          'Three SIM only deals', 'unlimited SIM only', 'no contract SIM only',
          'SIM only provider', 'best SIM only provider UK', 'mobile network provider',
          'cheap mobile provider', 'pay monthly SIM provider', 'data SIM provider',
        ]}
        canonical="https://www.siluxmobile.co.uk/sim-only-deals"
        breadcrumbs={breadcrumbs}
        faqs={FAQS}
      />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }} />

      <section className="py-20 px-4 bg-gradient-to-br from-primary-700 to-blue-600 text-white text-center">
        <div className="max-w-4xl mx-auto">
          <div className="inline-flex items-center gap-2 bg-white/20 rounded-full px-4 py-2 mb-6 text-sm font-medium">
            <Star className="w-4 h-4 text-yellow-400 fill-yellow-400" />
            Best SIM Only Deals on Three 5G
          </div>
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            SIM Only Deals UK
          </h1>
          <p className="text-lg text-white/80 max-w-2xl mx-auto mb-8">
            The UK's best SIM only deals on Three's 5G network. As a leading SIM only provider and mobile network provider,
            Silux Mobile lets you keep your phone, ditch the contract,
            and get 5G + Go Roam in 71+ countries from just £5/month.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/sim-only" className="px-8 py-4 bg-white text-primary-700 rounded-2xl font-bold shadow-lg hover:shadow-xl transition-all">
              See All Plans
            </Link>
            <Link to="/sim-only" className="px-8 py-4 bg-white/20 border-2 border-white/40 text-white rounded-2xl font-bold hover:bg-white/30 transition-all">
              Free SIM Card
            </Link>
          </div>
          <div className="flex flex-wrap justify-center gap-6 mt-10 text-sm text-white/70">
            <span className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-400" /> 5G Included</span>
            <span className="flex items-center gap-2"><Globe className="w-4 h-4 text-blue-300" /> 71+ Countries Roaming</span>
            <span className="flex items-center gap-2"><Shield className="w-4 h-4 text-green-400" /> 30-Day Rolling or Contract</span>
          </div>
        </div>
      </section>

      <SiluxPromise variant="sim" />

      <section className="py-16 px-4 bg-white">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-2xl font-bold text-gray-900 text-center mb-8">Choose Your SIM Only Plan</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {PLAN_TYPES.map(plan => (
              <Link
                key={plan.slug}
                to={plan.slug}
                className="group flex gap-4 p-6 bg-gray-50 rounded-2xl border border-gray-200 hover:border-primary-300 hover:shadow-md transition-all hover:-translate-y-1"
              >
                <plan.icon className="w-10 h-10 text-primary-600 flex-shrink-0" />
                <div>
                  <h3 className="font-bold text-gray-900 mb-1 group-hover:text-primary-600 transition-colors">{plan.name}</h3>
                  <p className="text-sm text-gray-600">{plan.desc}</p>
                  <span className="inline-flex items-center gap-1 mt-3 text-sm font-semibold text-primary-600">
                    View plans <ArrowRight className="w-3 h-3" />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 px-4 bg-gray-50">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-2xl font-bold text-gray-900 text-center mb-8">SIM Only Deal FAQs</h2>
          <div className="space-y-4">
            {FAQS.map(faq => (
              <details key={faq.question} className="group border border-gray-200 rounded-xl overflow-hidden bg-white">
                <summary className="flex items-center justify-between p-5 cursor-pointer font-semibold text-gray-900">
                  {faq.question}
                  <ArrowRight className="w-4 h-4 text-gray-400 group-open:rotate-90 transition-transform" />
                </summary>
                <div className="px-5 pb-5 text-gray-600 text-sm">{faq.answer}</div>
              </details>
            ))}
          </div>
        </div>
      </section>

      <section className="py-10 px-4 bg-white">
        <div className="max-w-5xl mx-auto text-center">
          <h2 className="text-lg font-bold text-gray-900 mb-4">Related SIM Card Pages</h2>
          <div className="flex flex-wrap justify-center gap-3">
            {[
              { label: 'Cheap SIM Cards', to: '/cheap-sim-cards' },
              { label: 'Data SIM Cards', to: '/data-sim-cards' },
              { label: 'No Contract SIM', to: '/no-contract-sim' },
              { label: 'Free SIM Card', to: '/sim-only' },
              { label: 'Multi-SIM Plans', to: '/sim-only/multisim' },
              { label: 'Long-Life SIM', to: '/sim-only/longlife-sim' },
              { label: 'Roaming SIM', to: '/roaming' },
              { label: 'Mobile Broadband', to: '/mobile-broadband' },
            ].map(link => (
              <Link key={link.to} to={link.to} className="px-4 py-2 bg-gray-100 text-gray-700 rounded-full text-sm hover:bg-primary-50 hover:text-primary-700 transition-all">
                {link.label}
              </Link>
            ))}
          </div>
        </div>
      </section>
    </>
  )
}
